import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { combineLatest } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-gas-costs-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './gas-costs-chart.component.html',
  styleUrl: './gas-costs-chart.component.css'
})
export class GasCostsChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  labels: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  invoiceAmountActualLabel: string = "Invoice Amount (Actual)";
  invoiceAmountForecastLabel: string = "Invoice Amount (Forecast)";

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyinvoiceAmountActualData: (number | null)[] = [120, 100, 128, 125, 130, 132, null, null, null, null, null, null];
  dummyinvoiceAmountForecastData: (number | null)[] = [null, null, null, null, null, null, 125, 110, 135, 142, 10, 120];

  @Input() invoiceAmountActualData: (number | null)[] = [120, 100, 128, 125, 130, 132, null, null, null, null, null, null];
  @Input() invoiceAmountForecastData: (number | null)[] = [null, null, null, null, null, null, 125, 110, 135, 142, 10, 120];

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('dashboard.invoice_amount_actual_gas'),
      this.translocoService.selectTranslate('dashboard.invoice_amount_forecast_gas')
    ]).subscribe((
      [
        invoiceAmountActualLabel,
        invoiceAmountForecastLabel,
      ]
    ) => {
      this.invoiceAmountActualLabel = invoiceAmountActualLabel;
      this.invoiceAmountForecastLabel = invoiceAmountForecastLabel;
    });

    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.labels = translations;
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.invoiceAmountActualData = this.dummyinvoiceAmountActualData;
      this.invoiceAmountForecastData = this.dummyinvoiceAmountForecastData;
    }
    Chart.register(ChartDataLabels);

    this.data = {
      labels: this.labels,
      datasets: [
        {
          type: 'bar',
          label: this.invoiceAmountActualLabel,
          backgroundColor: '#80A7A5',
          data: this.invoiceAmountActualData,
          borderRadius: 10,
          datalabels: {
            display: (ctx: any) => {
              const value = ctx.dataset.data[ctx.dataIndex];
              return value !== null ? 'auto' : false;
            },
            align: 'top',
            anchor: 'auto',
            color: '#000000',
            font: {
              size: 12,
              weight: 'normal',
              family: 'Gilroy',
            },
            textAlign: 'center',
            formatter: (value: any) => { if (!value || value === 0) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0') || value}€` },
          },

        },
        {
          type: 'bar',
          label: this.invoiceAmountForecastLabel,
          backgroundColor: '#C0D3D2',
          data: this.invoiceAmountForecastData,
          borderRadius: 10,
          datalabels: {
            display: (ctx: any) => {
              const value = ctx.dataset.data[ctx.dataIndex];
              return value !== null ? 'auto' : false;
            },
            align: 'top',
            anchor: 'auto',
            color: '#000000',
            font: {
              size: 12,
              weight: 'normal',
              family: 'Gilroy',
            },
            textAlign: 'center',
            formatter: (value: any) => { if (!value || value === 0) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0') || value}€` },
          },
        }
      ]
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            generateLabels: (chart: any) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset: any, index: any) => {
                return {
                  text: dataset.label,
                  fillStyle: dataset.type === 'bar' ? dataset.backgroundColor : dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  pointStyle: dataset.type === 'line' ? (dataset.borderDash ? 'dash' : 'line') : 'circle',
                  lineDash: dataset.type === 'line' && dataset.borderDash ? [2, 2, 2] : [],
                  lineWidth: dataset.type === 'line' ? 4 : 0,

                  hidden: !chart.isDatasetVisible(index),
                  datasetIndex: index,
                };
              });
            },
            font: {
              size: 12,
            },
            color: '#000000',
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              let label = context.dataset.label || '';
              let value = context.raw || 0;
              return `${label}: ${this.decimalPipe.transform(value, '1.0-0') || value}`;
            }
          }
        },
      },
      scales: {
        x: {
          title: {
            display: true,
          },
          grid: {
            display: false,
          },
          stacked: true,
        },
        y: {
          grid: {
            display: true,
          },
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
          stacked: true,
        }
      }
    };
  }
}
